import { ApiError, api } from '../../lib/api'
import {
  CATEGORY_PUBLICITY,
  GROUP_TYPE,
  SURVEY_QUESTION_TYPE,
  SURVEY_TYPE as DOMAIN_SURVEY_TYPE,
} from '../../domain/domainCodes'
import { MAX_PAGE_SIZE, readDataTable } from '../../lib/dataTable'
import { download } from '../../lib/download'
import { extensionOf, fileToBase64 } from '../../lib/file'
import { toFormData } from '../../lib/form'
import { isEnglish } from '../../lib/i18n'
import { upload } from '../../lib/upload'

/**
 * Awareness / policies / surveys / assessments.
 *
 * Nothing in this module reads `src/data/engagement.js` any more — every list, every save
 * and every toggle below reaches the API. Each section names its endpoints above itself.
 */

const label = (arabic, english) => (isEnglish ? english : arabic) || arabic || english || ''

/**
 * UTF-8 safe base64 — `btoa` alone throws on any character above U+00FF, which every
 * Arabic string is.
 */
function toBase64(text) {
  if (!text) return ''
  const bytes = new TextEncoder().encode(text)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

/**
 * The other direction, and it is needed because the two READ endpoints disagree.
 *
 * `POST .../list` and the admin page payload return `HtmlContentAr` still BASE64
 * (`GetAwarenessListHandler:50`, `ViewAwarenessManagementPageHandler:54`), while
 * `GET /api/portal/awareness/` returns the same column DECODED. `API_FINDINGS.md` §38
 * documents only the save side, so the admin read was taken as HTML — and every edit then
 * re-encoded what was already encoded. The server decodes once, and the stored article
 * became its own base64: a one-click, silent loss of content nobody can get back.
 *
 * Tolerant on purpose: a value that is not base64 is passed through unchanged, so this
 * keeps working if the endpoint is ever made consistent with the learner's.
 */
function fromBase64(value) {
  if (!value) return ''
  // Cheap shape test first — decoding everything would mangle ordinary HTML.
  if (!/^[A-Za-z0-9+/\s]+={0,2}$/.test(value.trim())) return value

  try {
    const binary = atob(value.trim())
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0))
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    /* Not base64 after all, or not UTF-8 — the raw value is the better answer. */
    return value
  }
}

/**
 * The option lists the three engagement forms offer, from the data the app
 * already reads — no new endpoint, and they stay in step with the lists.
 */
/**
 * The option lists the three engagement forms offer.
 *
 * The GROUPS are real now — they come with the awareness payload, which is the one call
 * these screens already make. The categories are still the mock's, and belong to the
 * surveys and assessments slices.
 */
export async function getEngagementOptions() {
  /* THE GROUPS COME FROM THE SURVEYS PAGE, NOT THE AWARENESS ONE.
   *
   * This read `getAwarenessPage()`, which is guarded by `AwarenessContentManagers`. The one
   * caller is the SURVEY form, so a `SurveyManagers` account got a 403 — swallowed by the
   * `.catch` below — and the audience picker rendered empty, reading as "this installation
   * has no groups". `/api/admin/surveys-management/` now carries the same list behind the
   * role the form's own screen already requires. */
  const [page, content] = await Promise.all([
    getSurveysPage().catch(() => ({ groups: [] })),
    import('../../data/content'),
  ])
  return {
    groups: page.groups.map((group) => ({ value: String(group.value), label: group.label })),
    categories: content.categories.map((category) => ({
      value: String(category.id),
      label: category.name,
      publicity: category.publicity,
    })),
  }
}

/** Audience choices for the awareness form, read through its own role boundary. */
export async function getAwarenessOptions(signal) {
  const page = await getAwarenessPage(signal)
  return { groups: page.groups }
}

/* ------------------------------------------------------------------ *
 * Awareness - `Features/Admin/AwarenessManagement/Endpoint.cs`,
 * `AwarenessContentManagers`.
 *
 *   getAwarenessPage   -> GET  /api/admin/awareness-management/
 *   listAwareness      -> POST .../list                form: AwarenessId, IsActive
 *   getAwareness       -> POST .../list                the same call, one id
 *   saveAwareness      -> POST .../save                form
 *   listAttachments    -> POST .../attachments         form: AwarenessId
 *   addAttachment      -> POST .../add-attachment
 *   updateAttachment   -> POST .../update-attachment
 *   removeAttachment   -> POST .../remove-attachment
 *   listViews          -> POST .../views               form + DataTables keys
 *   saveScreenSettings -> POST .../save-settings
 *
 * THE IMAGE IS BASE64 IN A FORM FIELD, not a file part — `ImageBase64` alongside
 * `ImageName` and `ImageType`. A fourth convention: topics match parts by filename,
 * programmes take base64 in JSON, questions take real parts, and this takes base64 inside
 * form-encoding.
 *
 * `Groups` is a COMMA-SEPARATED STRING of ids, not a list.
 * ------------------------------------------------------------------ */

const AWARENESS = '/api/admin/awareness-management'

export async function exportAwareness(id) {
  return download(`${AWARENESS}/export/${encodeURIComponent(id)}`)
}

/** The list, the groups it can be assigned to, and the screen's own settings. */
export async function getAwarenessPage(signal) {
  const dto = (await api.get(`${AWARENESS}/`, { signal })) ?? {}

  return {
    rows: (dto.AwarenessList ?? []).map(toAwarenessRow),
    groups: (dto.GroupsList ?? []).map((group) => ({
      value: group.Id ?? group.GroupId,
      label: label(group.ArabicName, group.EnglishName),
    })),
    settings: {
      enabled: Boolean(dto.IsAwarenessContentEnabled),
      notify: Boolean(dto.IsAwarenessNotificationActive),
      screenNameAr: dto.AwarenessScreenNameAr ?? '',
      screenNameEn: dto.AwarenessScreenNameEn ?? '',
    },
  }
}

function toAwarenessRow(dto) {
  return {
    id: dto.Id,
    /* DISPLAY title, for the table — whichever language the panel is in. */
    title: label(dto.TitleAr, dto.TitleEn),
    /* EDIT titles, per language. Saving `title` back into `TitleAr` would write the
       English one over the Arabic whenever the panel is in English. */
    titleAr: dto.TitleAr ?? '',
    titleEn: dto.TitleEn ?? '',
    /* BASE64 on the way in as well as out — see `fromBase64`. Taking these as HTML is what
       made an edit re-encode the article and destroy it. */
    content: fromBase64(dto.HtmlContentAr),
    contentEn: fromBase64(dto.HtmlContentEn),
    /* Who wrote it, in whichever language the panel is in. */
    user: label(dto.UserFullNameArabic, dto.UserFullNameEnglish),
    createdOn: dto.Creation ?? '',
    lastUpdate: dto.LastUpdate ?? '',
    isActive: Boolean(dto.IsActive),
    groupIds: (dto.Groups ?? []).map((group) => group.Id ?? group),
  }
}

export async function listAwareness({ isActive = null, signal } = {}) {
  const fields = {}
  if (isActive !== null && isActive !== '') fields.IsActive = String(isActive)

  const rows = (await api.post(`${AWARENESS}/list`, toFormData(fields), { signal })) ?? []
  const mapped = rows.map(toAwarenessRow)
  return { rows: mapped, total: mapped.length }
}

/**
 * `PolicyDataDto` — the same list the legacy settings screen draws, and the source of the
 * policy ids `listPolicyUsers` needs.
 */
/**
 * The stored document, as an address the browser can open.
 *
 * TWO THINGS HAVE TO BE FIXED UP HERE.
 *
 * THE DIRECTORY IS WRONG ON THIS PAYLOAD. `GetPoliciesListHandler` composes
 * `root/Policies/…`, and there is no such directory: `SavePolicyHandler` writes to
 * `wwwroot/root/WorkPlacePolicies/`, which is also where the delete, the learner's list
 * and the learner's download all look. This one handler declares the odd constant, so
 * every file link on the admin list answered 404. API_FINDINGS.md §106.
 *
 * THE PATH IS COMPOSED WHETHER OR NOT THERE IS A FILE, so a policy stored without one
 * arrives as `root/Policies/.` — a link to a directory. Only a path with a real name is
 * a document; the row then offers nothing for that language.
 */
const POLICY_DIRECTORY = 'root/WorkPlacePolicies'

const policyFileUrl = (path) => {
  const value = String(path ?? '').trim().replace(/^\/+/, '')
  const name = value.split('/').pop() ?? ''
  if (!value || !name.includes('.') || name.startsWith('.')) return ''
  return `/${POLICY_DIRECTORY}/${name}`
}

export async function listPolicies() {
  const dto = (await api.post('/WorkspacePoliciesSettings/GetPoliciesList')) ?? []

  const rows = dto.map((policy) => ({
    id: policy.Id,
    name: label(policy.ArabicName, policy.EnglishName),
    nameEn: policy.EnglishName ?? '',
    version: policy.Version ?? 0,
    createdOn: policy.CreationDate ?? '',
    forceUsers: Boolean(policy.ForceUsers),
    evaluationStartDate: policy.EvaluationStartDate || null,
    isActive: Boolean(policy.Enabled),
    readOnly: Boolean(policy.ReadOnly),
    state: policy.StateString ?? '',
    /* The two documents themselves — `root/WorkPlacePolicies/{PathMap}.{FileType}`, served
       straight out of wwwroot. The legacy row menu opens each in a new tab; so does this. */
    fileAr: policyFileUrl(policy.ArFile),
    fileEn: policyFileUrl(policy.EnFile),
    groups: (policy.Groups ?? []).map((group) => group.Id),
    /* Not on the payload. The count of users a policy reaches is what
       `listPolicyUsers` answers, one policy at a time. */
    users: null,
  }))

  return { rows, total: rows.length }
}

/**
 * One policy, every user it was issued to — `UsersPoliciesDetails/GetDetails`.
 *
 * THE ROWS ARE POSITIONAL. `UserPolicyDetailsResult.Data` is `List<List<string>>`, in this
 * order, with the first cell an HTML anchor and the last the user's id:
 *
 *   0 <a href='/UserDetailsAdmin?Id={guid}'>{full name or username}</a>
 *   1 full name   2 policy version   3 download status   4 download date
 *   5 acceptance status   6 acceptance date   7 user id
 *
 * Both status cells arrive ALREADY LOCALIZED, and the acceptance wording depends on the
 * policy: a read-only policy is "viewed / not viewed", one requiring consent is
 * "acknowledged / rejected / not acknowledged". So they are shown as received (W6) rather
 * than mapped here, where that distinction is not known.
 */
/**
 * The people who have NOT opened the policy — `WorkspacePoliciesSettings/GetDetailsNotViewed`.
 *
 * A SEPARATE QUERY, and it has to be. `GetDetails` returns only users who already have a
 * `UserPolicyDetail` row, i.e. people who have engaged with the policy at all. Someone who
 * has never opened it has no such row, so filtering that list for "not viewed" could never
 * contain them — the drawer's third scope was filtering a set the answer is not in. This
 * endpoint asks the opposite question and answers it directly.
 *
 * PARAMETERS IN THE QUERY STRING: the handler takes `int policyId` with no `[FromBody]`, so
 * a JSON body binds 0 and the answer is an empty list rather than an error.
 */
export async function listPolicyUsersNotViewed(policyId, signal) {
  try {
    const query = new URLSearchParams({
      policyId: String(Number(policyId) || 0),
      draw: '1',
      start: '0',
      length: String(MAX_PAGE_SIZE),
    })
    const dto = await api.post(
      `/WorkspacePoliciesSettings/GetDetailsNotViewed?${query}`,
      undefined,
      { signal },
    )
    const rows = (dto?.Data ?? dto?.data ?? []).map((cells) => toPendingPolicyUserRow(cells))
    return { rows, total: dto?.RecordsTotal ?? dto?.recordsTotal ?? rows.length, message: '' }
  } catch (error) {
    if (error instanceof ApiError && error.code === 'RESULT') {
      return { rows: [], total: 0, message: error.message }
    }
    throw error
  }
}

export async function listPolicyUsers(policyId, signal) {
  try {
    const dto = await api.post('/UsersPoliciesDetails/GetDetails', { PolicyId: Number(policyId) }, { signal })
    const rows = (dto?.Data ?? []).map((cells) => toPolicyUserRow(cells))
    return { rows, total: rows.length, message: '' }
  } catch (error) {
    /* A policy nobody has been issued answers `{ Result: false, Message }`, which `api.js`
       turns into an ApiError. That is an EMPTY LIST, not a failure — the drawer shows the
       server's sentence in its empty state. A real failure still throws. */
    if (error instanceof ApiError && error.code === 'RESULT') {
      return { rows: [], total: 0, message: error.message }
    }
    throw error
  }
}

/**
 * A row of the not-acknowledged list: `[#, name, email, status, standing]`. Its own shape —
 * it was read through the full-list mapper, which put the email in the version column.
 */
function toPendingPolicyUserRow(cells) {
  const [, fullName, email, acceptStatus, standing] = cells
  return {
    id: email || fullName,
    userId: '',
    name: fullName ?? '',
    version: '',
    downloadStatus: '',
    downloadDate: '',
    acceptStatus: acceptStatus ?? '',
    acceptDate: '',
    standing: standing ?? '',
  }
}

/**
 * `<a href='…?Id=…'>Name</a>` -> the id and the name. Parsed, never rendered. The last cell is
 * the person's standing on the CURRENT version as a code — acknowledged · rejected ·
 * downloadedOnly · olderVersionOnly · notOpened — the same standing the export and the
 * Reports page use.
 */
function toPolicyUserRow(cells) {
  const [link, fullName, version, downloadStatus, downloadDate, acceptStatus, acceptDate, userId, standing] = cells

  const display = String(link ?? '').replace(/<[^>]*>/g, '').trim()

  return {
    id: userId || display,
    userId: userId || '',
    name: fullName || display,
    version: version ?? '',
    downloadStatus: downloadStatus ?? '',
    downloadDate: downloadDate ?? '',
    acceptStatus: acceptStatus ?? '',
    acceptDate: acceptDate ?? '',
    standing: standing ?? '',
  }
}

/**
 * @param {'excel'|'pdf'|'doc'} format
 * @param {boolean|null} isViewed  narrows to those who have (or have not) seen it. The LIST
 *        endpoint takes no filters and the EXPORT does, so whatever narrows the table on
 *        screen has to be repeated here or the file will not match it.
 */
export async function exportPolicyUsers({ policyId, format, isViewed = null, search = '' }) {
  const query = new URLSearchParams({ policyId: String(policyId), type: format })
  if (isViewed !== null) query.set('isViewed', String(isViewed))
  if (search) query.set('search', search)

  try {
    const saved = await download(`/UsersPoliciesDetails/Export?${query}`)
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/* ------------------------------------------------------------------ *
 * Policies, the rest of the slice - `WorkspacePoliciesSettings`, `PoliciesManagers`.
 *
 * EVERY ACTION HERE TAKES ITS ARGUMENTS IN THE QUERY STRING. The handlers declare bare
 * primitives (`int policyId`, `bool state`, `string titleAr`) and minimal APIs bind those
 * from the route or the query, never the body — and being non-nullable they are REQUIRED,
 * so a JSON body is not ignored, it is a 400 (API_FINDINGS.md §40). The two that take a
 * command object — AddNotification, SavePolicyCategory — are the only ones posting JSON.
 *
 *   getPoliciesPage      -> GET  /api/admin/workspace-policies/
 *   savePolicy           -> POST /Save                            MULTIPART, two PDFs
 *   deletePolicy         -> POST /Delete?policyId=
 *   setPolicyState       -> POST /UpdatePolicyState?id=&state=
 *   setPoliciesEnabled   -> POST /UpdatePoliciesEnabled?isEnabled=
 *   setPoliciesViewing   -> POST /UpdatePoliciesViewing?type=
 *   setExternalPolicyUrl -> POST /UpdateExternalPoliciesUrl?externalPoliciesUrl=
 *   setPoliciesPageTitle -> POST /UpdatePageTitle?titleAr=&titleEn=
 *   listPolicyCategories -> POST /FilterPolicyCategory
 *   savePolicyCategory   -> POST /SavePolicyCategory               JSON, the whole command
 *   deletePolicyCategory -> POST /DeletePolicyCategory?id=
 *
 * NOT HERE: mailing a policy to its groups. The legacy `SendEmail` has no counterpart, and
 * that button is registered in COSMETIC_UI.md.
 */
const POLICIES = '/WorkspacePoliciesSettings'

/** POST with everything in the query, which is how this slice binds. */
async function policyAction(path, params = {}) {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) query.set(key, String(value))
  const suffix = query.toString() ? `?${query}` : ''

  try {
    const data = await api.post(`${POLICIES}/${path}${suffix}`)
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/**
 * The screen's own settings, from the query that used to render the Razor page.
 *
 * `IsPoliciesEnabled` and `ShowPolicyCategoriesAsGrid` arrive as the STRINGS "true"/"false" —
 * they are settings rows, and the handler passes the stored text through.
 */
export async function getPoliciesPage(signal) {
  const dto = (await api.get('/api/admin/workspace-policies/', { signal })) ?? {}

  return {
    enabled: String(dto.IsPoliciesEnabled).toLowerCase() === 'true',
    /* The two ways a user meets the policies: as a grid of categories, or as one flat
       list. The API stores the choice under `ShowPolicyCategoriesAsGrid`. */
    showAsGrid: String(dto.ShowPolicyCategoriesAsGrid).toLowerCase() === 'true',
    externalUrl: dto.ExternalPoliciesUrl ?? '',
    pageTitleAr: dto.PoliciesPageTitleAr ?? '',
    pageTitleEn: dto.PoliciesPageTitleEn ?? '',
    /* Whether a template exists for each — the switches on the legacy's settings tab
       read exactly this. */
    newPolicyNotification: Boolean(dto.IsNewPolicyNotificationActive),
    agreementNotification: Boolean(dto.IsPolicyAgreementRequirementNotification),
    categories: (dto.Categories ?? []).map(toPolicyCategoryRow),
    groups: (dto.Groups ?? []).map((group) => ({
      value: group.GroupId,
      label: label(group.ArabicName, group.EnglishName),
    })),
  }
}

const toPolicyCategoryRow = (dto) => ({
  id: dto.Id,
  name: label(dto.NameArabic, dto.NameEnglish),
  nameAr: dto.NameArabic ?? '',
  nameEn: dto.NameEnglish ?? '',
  isActive: Boolean(dto.Enabled),
})

/**
 * Add or edit a policy - `POST /WorkspacePoliciesSettings/Save`.
 *
 * MULTIPART, not JSON and not a query: a policy carries two PDF documents, so this is the
 * one action in the slice that posts a body of parts. `Id` of 0 adds. On an edit a file left
 * out keeps the one already stored, which is why neither is appended unless it was chosen.
 */
export async function savePolicy(values) {
  const body = new FormData()
  body.append('Id', String(Number(values.id ?? 0)))
  body.append('ArabicName', values.name ?? '')
  body.append('EnglishName', values.nameEn ?? '')
  body.append('Version', String(Number(values.version ?? 1)))
  body.append('ReadOnly', String(Boolean(values.readOnly)))
  /* A read-only policy enforces nothing, and the API stores both as null for one - sent
     to match rather than left to disagree. */
  body.append('ForceUsers', String(!values.readOnly && Boolean(values.forceUsers)))
  if (!values.readOnly && values.evaluationStartDate) {
    body.append('EvaluationStartDate', values.evaluationStartDate)
  }
  if (values.categoryId) body.append('PolicyCategoryId', String(Number(values.categoryId)))
  body.append('GroupIds', (values.groups ?? []).map(Number).join(','))
  if (values.fileAr) body.append('ArabicFile', values.fileAr)
  if (values.fileEn) body.append('EnglishFile', values.fileEn)

  try {
    const data = await api.post(`${POLICIES}/Save`, body)
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export const deletePolicy = (policyId) => policyAction('Delete', { policyId: Number(policyId) })

export const setPolicyState = (id, state) =>
  policyAction('UpdatePolicyState', { id: Number(id), state: Boolean(state) })

export const setPoliciesEnabled = (isEnabled) =>
  policyAction('UpdatePoliciesEnabled', { isEnabled: Boolean(isEnabled) })

/** `type` is the stored text, not a number: "true" shows the category grid. */
export const setPoliciesViewing = (showAsGrid) =>
  policyAction('UpdatePoliciesViewing', { type: showAsGrid ? 'true' : 'false' })

export const setExternalPolicyUrl = (url) =>
  policyAction('UpdateExternalPoliciesUrl', { externalPoliciesUrl: url ?? '' })

export const setPoliciesPageTitle = (titleAr, titleEn) =>
  policyAction('UpdatePageTitle', { titleAr: titleAr ?? '', titleEn: titleEn ?? '' })

export const deletePolicyCategory = (id) => policyAction('DeletePolicyCategory', { id: Number(id) })

/** A DataTables list, but the screen shows every category — there are a handful. */
export async function listPolicyCategories(signal) {
  const dto = await api.post(`${POLICIES}/FilterPolicyCategory?start=0&length=100`, undefined, { signal })
  const { rows, total } = readDataTable(dto)
  return { rows: rows.map(toPolicyCategoryRow), total }
}

/** The one policy action that posts a body: `SavePolicyCategoryCommand` binds as JSON. */
export async function savePolicyCategory(values) {
  try {
    const data = await api.post(`${POLICIES}/SavePolicyCategory`, {
      Id: Number(values.id ?? 0),
      NameArabic: values.nameAr ?? '',
      NameEnglish: values.nameEn ?? '',
      Enabled: Boolean(values.isActive),
    })
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/* ------------------------------------------------------------------ *
 * Surveys - `Features/Admin/SurveyManagement/` and its `SurveyModal`,
 * both `SurveyManagers`.
 *
 *   getSurveysPage  -> GET  /api/admin/surveys-management/
 *   listSurveys     -> POST /SurveysManagement/GetData             { Id }
 *   deleteSurvey    -> POST /SurveysManagement/Delete              { Id }
 *   setSurveysEnabled -> POST /SurveysManagement/UpdateSurveyFeatureState
 *   getSurvey       -> POST /SurveyModal/GetDataRow                { Id }
 *   saveSurvey      -> POST /SurveyModal/ProcessSubmit             JSON, the whole command
 *   coursesForSurvey / programsForSurvey -> POST /SurveyModal/{GetCourses,GetPrograms}
 *
 * `ProcessSubmit` binds `SaveSurveyCommand` STRAIGHT FROM THE BODY as JSON - no form, no
 * wrapper - which makes it the only save in Phase 6 that is not form-encoded.
 *
 * A survey has a TYPE, and the type decides what else it needs: a course survey wants a
 * course, a programme survey a programme, and a general one neither. The two lookups exist
 * for exactly that.
 * ------------------------------------------------------------------ */

const SURVEYS = '/SurveysManagement'
const SURVEY_MODAL = '/SurveyModal'

/** Whether surveys are offered at all, the two notification switches, and the audience. */
export async function getSurveysPage(signal) {
  const dto = (await api.get('/api/admin/surveys-management/', { signal })) ?? {}

  return {
    enabled: Boolean(dto.SurveyEnabled),
    notify: Boolean(dto.SurveyNotificationActive),
    notifyMandatory: Boolean(dto.SurveyMandatoryNotificationActive),
    groups: (dto.GroupsList ?? []).map((group) => ({
      value: group.Id ?? group.GroupId,
      label: label(group.ArabicName, group.EnglishName),
    })),
  }
}

function toSurveyRow(dto) {
  return {
    id: dto.Id,
    title: dto.Title || label(dto.ArabicName, dto.EnglishName),
    /* Both, per language - the display `title` above is one of them, and saving it back
       would overwrite the other. */
    titleAr: dto.ArabicName ?? '',
    titleEn: dto.EnglishName ?? '',
    descriptionAr: dto.ArabicDescription ?? '',
    descriptionEn: dto.EnglishDescription ?? '',
    timeRestricted: Boolean(dto.IsTimeRestricted),
    /* DATE-ONLY, because that is what `DatePicker` reads and writes. The payload carries a
       full `DateTime` (`2024-01-01T00:00:00`), which a `<input type="date">` cannot show —
       same trim `licenseService` does with its licence dates. */
    startDate: dto.StartDate ? String(dto.StartDate).slice(0, 10) : '',
    endDate: dto.EndDate ? String(dto.EndDate).slice(0, 10) : '',
    createdOn: dto.CreatedDateString ?? '',
    /* BOTH NAMES. The table's column is `updatedOn` and the form reads `lastUpdate`; only
       the second was mapped, so `تاريخ التعديل` was blank on every row while the payload
       carried the date all along. */
    lastUpdate: dto.LastUpdateDateString ?? '',
    updatedOn: dto.LastUpdateDateString ?? '',
    isActive: Boolean(dto.Enabled),
    /* The FORM calls it `enabled`; the table calls it `isActive`. Both are carried rather
       than making either screen translate. */
    enabled: Boolean(dto.Enabled),
    mandatory: Boolean(dto.Mandatory),
    /* The assessment this one follows up — the pair the Reports page compares (KN-05). */
    baselineId: dto.BaselineAssessmentId ? String(dto.BaselineAssessmentId) : '',
    finished: Boolean(dto.Finished),
    /* Whether anyone has answered - the screen must not let a survey with answers be
       reshaped underneath them. */
    hasAnswers: Boolean(dto.HasAnswers),
    type: dto.SurveyType ?? DOMAIN_SURVEY_TYPE.GENERAL,
    typeName: dto.SurveyTypeString ?? '',
    restriction: dto.SurveyRestrictionMethod ?? null,
    groupIds: dto.GroupIds ?? [],
    /* The form's switch for "only these departments" — implied by there being any. */
    restrictToGroups: (dto.GroupIds ?? []).length > 0,
  }
}

export async function listSurveys({ activeOnly = false, signal } = {}) {
  const rows = (await api.post(`${SURVEYS}/GetData`, { Id: 0 }, { signal })) ?? []
  const mapped = rows.map(toSurveyRow)

  return {
    rows: activeOnly ? mapped.filter((row) => row.isActive) : mapped,
    total: mapped.length,
  }
}

/**
 * One survey with its questions, for the editor.
 *
 * `surveyId` GOES IN THE QUERY STRING even though this is a POST: the handler takes a
 * primitive (`int surveyId`) with no attribute, and minimal APIs bind those from the query,
 * never the body. It is also non-nullable, so it is REQUIRED — an id sent in the body is
 * rejected 400 before the handler runs, which is why the editor used to open empty.
 */
export async function getSurvey(id, signal) {
  const dto = await api.post(`${SURVEY_MODAL}/GetDataRow?surveyId=${Number(id)}`, undefined, { signal })
  if (!dto) return null

  const survey = dto.Survey ?? dto

  /* `toSurveyRow` describes a TABLE ROW, and two of its fields are second names for
     something the form edits under a different one: `titleAr` beside the form's `title`,
     `isActive` beside its `enabled`. Dropped rather than carried, because form state with
     two keys for one value saves whichever the payload happens to read — which is how an
     edited Arabic title was posted back at its old value. */
  const { titleAr: _titleAr, isActive: _isActive, ...row } = toSurveyRow(survey)

  return {
    ...row,
    /* The form edits `title` as the ARABIC name, beside `titleEn`. The table's `title` is
       whichever language is showing, so seeding from it would write the English name into
       the Arabic column. */
    title: survey.ArabicName ?? '',
    /* A course or programme survey cannot be saved without the thing it is attached to —
       the validator rejects it — so the current attachment travels with the form. */
    courseId: survey.CourseId ?? null,
    programId: survey.ProgramId ?? null,
    questions: (dto.Questions ?? survey.Questions ?? []).map((question, index) => ({
      id: question.Id ?? -(index + 1),
      textAr: question.ArabicText ?? '',
      textEn: question.EnglishText ?? '',
      type: question.QuestionType ?? question.Type ?? SURVEY_QUESTION_TYPE.RADIO,
      mandatory: Boolean(question.IsMandatory),
      answers: (question.Answers ?? []).map((answer, answerIndex) => ({
        id: answer.Id ?? -(answerIndex + 1),
        textAr: answer.ArabicText ?? '',
        textEn: answer.EnglishText ?? '',
      })),
    })),
  }
}

/** `SurveyType` — the numbers the API stores, named. */
export const SURVEY_TYPE = Object.freeze({
  general: DOMAIN_SURVEY_TYPE.GENERAL,
  course: DOMAIN_SURVEY_TYPE.COURSE,
  program: DOMAIN_SURVEY_TYPE.PROGRAM,
})

/**
 * Whether a programme or course already HAS a survey — `CheckSurveyForType`.
 *
 * The legacy asks this before opening its survey modal and branches on the answer:
 * `showSurveyModalForCreate()` when the id comes back zero, `showSurveyModalForUpdate(id)`
 * otherwise. A programme has at most one survey, so this is the whole decision.
 *
 * `surveyType` is a STRING here — the handler lowercases it and compares against
 * "program" / "course", not against the numeric SURVEY_TYPE.
 */
export async function findSurveyFor(surveyType, targetId, signal) {
  try {
    const dto = await api.post(
      `${SURVEY_MODAL}/CheckSurveyForType`,
      { SurveyType: surveyType, TargetId: Number(targetId) },
      { signal },
    )
    return Number(dto?.SurveyId ?? 0)
  } catch (error) {
    if (error instanceof ApiError) return 0
    throw error
  }
}

/** The courses / programmes a survey of that type can be attached to. */
async function surveyTargets(path, signal) {
  try {
    /* A BODY, and one that says which language. Both handlers take their query as an
       implicit body parameter (`GetCoursesForSurveyQuery query`), so a POST with no body at
       all is refused before the handler runs — "Implicit body inferred for parameter query
       but no body was provided". And `IsArabic` is read from that body rather than from the
       request's culture, so sending nothing meant `false`, and an Arabic panel listed
       English names. */
    const body = { IsArabic: !isEnglish }
    const rows = (await api.post(`${SURVEY_MODAL}/${path}`, body, { signal })) ?? []
    /* `Title` — the one field `ProgramItemViewModel` and `CourseItemViewModel` actually
       carry. None of `Name`, `DisplayField` or `ArabicName` is on either, so every option
       came through with an empty label: a dropdown of blank rows you could pick from and not
       read. The others stay as fallbacks; `Title` is what these two answer with. */
    return rows.map((row) => ({
      value: row.Id ?? row.Value,
      label: row.Title ?? row.Name ?? row.DisplayField ?? label(row.ArabicName, row.EnglishName),
    }))
  } catch (error) {
    if (error instanceof ApiError) return []
    throw error
  }
}

export const coursesForSurvey = (signal) => surveyTargets('GetCourses', signal)
export const programsForSurvey = (signal) => surveyTargets('GetPrograms', signal)

/**
 * Create or update. JSON, not a form - `ProcessSubmit` binds the command itself.
 *
 * `OperationType` is 0 to add and 1 to update, alongside the id rather than derived from it.
 */
export async function saveSurvey(values) {
  const surveyType = Number(values.type ?? SURVEY_TYPE.general)
  const isGeneral = surveyType === SURVEY_TYPE.general

  const body = {
    Id: Number(values.id ?? 0),
    /* `values.title` IS the Arabic name — the field the form edits. It was written
       `values.titleAr ?? values.title`, and on an edit `titleAr` held the value as LOADED,
       so `??` took the stale one and every rename was posted back unchanged over a
       "saved successfully". One field, one source. */
    ArabicName: values.title ?? '',
    EnglishName: values.titleEn ?? '',
    ArabicDescription: values.descriptionAr ?? '',
    EnglishDescription: values.descriptionEn ?? '',
    IsTimeRestricted: Boolean(values.timeRestricted),
    StartDate: values.startDate || null,
    EndDate: values.endDate || null,
    Enabled: Boolean(values.enabled),
    Mandatory: Boolean(values.mandatory),
    SurveyType: surveyType,
    /* THE TYPE DECIDES THESE THREE, and the validator rejects any other combination: a
       general survey must carry no restriction and no attachment, a course survey must
       carry a course and no programme, a programme survey the reverse. Derived here so a
       stale value left over from switching the type cannot be posted. */
    SurveyRestrictionMethod: isGeneral || values.restriction == null ? null : Number(values.restriction),
    GroupIds: (values.groupIds ?? []).map(Number),
    CourseId: surveyType === SURVEY_TYPE.course && values.courseId != null ? Number(values.courseId) : null,
    ProgramId: surveyType === SURVEY_TYPE.program && values.programId != null ? Number(values.programId) : null,
    OperationType: Number(values.id ?? 0) > 0 ? 1 : 0,
    Questions: (values.questions ?? []).map((question) => ({
      Id: Number(question.id) > 0 ? Number(question.id) : 0,
      ArabicText: question.textAr ?? '',
      EnglishText: question.textEn ?? '',
      QuestionType: Number(question.type ?? SURVEY_QUESTION_TYPE.RADIO),
      /* Whether the respondent may skip it. The legacy asks per question and the sheet
         import carries it in its third column; it was never sent, so every question
         saved as optional. */
      IsMandatory: Boolean(question.mandatory),
      Answers: (question.answers ?? []).map((answer) => ({
        Id: Number(answer.id) > 0 ? Number(answer.id) : 0,
        ArabicText: answer.textAr ?? '',
        EnglishText: answer.textEn ?? '',
      })),
    })),
  }

  try {
    const data = await api.post(`${SURVEY_MODAL}/ProcessSubmit`, body)
    return { ok: data?.Result !== false, message: data?.Message ?? '', id: data?.Id ?? values.id }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/**
 * Questions from a sheet — `ProcessExcelQuestions`, the legacy modal's "upload questions
 * file". The server parses and validates; nothing is saved. What comes back is appended
 * to the form's list, and travels with the survey when that is saved.
 *
 * The file goes as the multipart part `excelFile` — the handler reads the form by that
 * name, not a JSON body. The template is `GET /SurveyModal/DownloadSurveyQuestionsTemplate`,
 * in the panel's language.
 *
 * @returns {Promise<{ok: boolean, message: string, questions: object[], skipped: {row: number, text: string, reasons: string[]}[]}>}
 *   `skipped` are the rows the server refused, each with its reasons, so the author can
 *   fix the sheet rather than wonder why the count is short.
 */
export async function importSurveyQuestions(file) {
  const form = new FormData()
  form.append('excelFile', file)

  try {
    const data = await upload(`${SURVEY_MODAL}/ProcessExcelQuestions`, form)
    if (data?.Result === false) return { ok: false, message: data?.Message ?? '', questions: [], skipped: [] }

    return {
      ok: true,
      message: data?.Message ?? '',
      questions: (data?.Data ?? []).map((row, index) => ({
        id: -(index + 1),
        textAr: row.QuestionArabic ?? '',
        textEn: row.QuestionEnglish ?? '',
        type: Number(row.Type ?? SURVEY_QUESTION_TYPE.RADIO),
        mandatory: Number(row.IsMandatory) === 1,
        answers: (Array.isArray(row.Answers) ? row.Answers : []).map((answer, answerIndex) => ({
          id: -(answerIndex + 1),
          textAr: answer.Arabic ?? '',
          textEn: answer.English ?? '',
        })),
      })),
      skipped: (data?.InvalidData ?? []).map((row) => ({
        row: Number(row.Row ?? 0),
        text: row.Cells?.[0] || row.Cells?.[1] || '',
        reasons: row.Reasons ?? [],
      })),
    }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message, questions: [], skipped: [] }
    throw error
  }
}

export const SURVEY_QUESTIONS_TEMPLATE_URL = `${SURVEY_MODAL}/DownloadSurveyQuestionsTemplate`

async function surveyAction(path, body) {
  try {
    const data = await api.post(`${SURVEYS}/${path}`, body)
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/**
 * EACH COMMAND NAMES THE ID DIFFERENTLY, and a wrong name binds to zero rather than
 * failing: `GetSurveysQuery` takes `Id`, `DeleteSurveyCommand` takes `SurveyId`,
 * `GetSurveyReportQuery` takes `SurveyId`, and `AddNotificationCommand` takes `Id` as a
 * STRING. Sending the wrong one deletes nothing and reports "not found" against survey 0.
 */
export const deleteSurvey = (id) => surveyAction('Delete', { SurveyId: Number(id) })

/** `IsEnabled`, not `Enabled`. */
export const setSurveysEnabled = (enabled) =>
  surveyAction('UpdateSurveyFeatureState', { IsEnabled: Boolean(enabled) })

/**
 * The answers to one survey, aggregated.
 *
 * NOT a `surveyAction` — this one returns DATA. `Results.Json(result.Data)` on success and
 * `{ Error }` on failure, which is the only place in the slice that reports a failure that
 * way rather than as `{ Result, Message }`.
 */
/**
 * The report as a workbook — `GET /SurveysManagement/DownloadSurveyReport?surveyId=`.
 *
 * The endpoint has always been there and nothing in `src/` called it, so the report drawer's
 * only control was its close button: the figures could be read on screen and not taken away.
 * Same `download` adapter every other export in this file uses, so the credential rides the
 * cookie rather than the URL.
 */
export async function downloadSurveyReport(surveyId) {
  try {
    const saved = await download(
      `${SURVEYS}/DownloadSurveyReport?surveyId=${encodeURIComponent(surveyId)}`,
    )
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export async function getSurveyReport(id, signal) {
  try {
    const dto = await api.post(`${SURVEYS}/GetSurveyReport`, { SurveyId: Number(id) }, { signal })
    if (!dto || dto.Error) return { ok: false, message: dto?.Error ?? '', questions: [], total: 0 }

    return {
      ok: true,
      message: '',
      total: dto.TotalAnswersCount ?? 0,
      questions: (dto.Questions ?? []).map((question) => ({
        question: question.Question ?? '',
        type: question.QuestionType ?? 0,
        answers: (question.Aggregation ?? []).map((row) => ({
          answer: row.Answer ?? '',
          count: row.ResponseCount ?? 0,
        })),
      })),
    }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message, questions: [], total: 0 }
    throw error
  }
}

/* ------------------------------------------------------------------ *
 * Assessments - `Features/Admin/AssessmentManagement/Endpoint.cs`,
 * `AssessmentManagers`.
 *
 *   getAssessmentsPage -> GET  /api/admin/assessments-management/
 *   listAssessments    -> POST .../list
 *   getAssessment      -> POST .../get      form: Id
 *   saveAssessment     -> POST .../save     form
 *   deleteAssessment   -> POST .../delete   form: assessmentId
 *   setAssessmentsEnabled -> POST .../update-feature-state
 *   getAssessmentReport   -> POST .../report          JSON, paged
 *   getAssessmentAnswers  -> POST .../report/answers  JSON
 *
 * `CategoryIds` and `GroupIds` are COMMA-SEPARATED STRINGS of ids, as in awareness next
 * door — not lists, and not repeated fields.
 * ------------------------------------------------------------------ */

const ASSESSMENTS = '/api/admin/assessments-management'

/**
 * The groups and topics an assessment draws from, and whether the feature is on.
 *
 * `Groups` is EVERY group, departments and special groups alike — an assessment can be set for
 * either. `kind` splits them for the picker's switch: a department is type 1, anything else is a
 * special group, the same cut the groups screen makes.
 */
export async function getAssessmentsPage(signal) {
  const dto = (await api.get(`${ASSESSMENTS}/`, { signal })) ?? {}

  return {
    enabled: Boolean(dto.AssessmentEnabled),
    notify: Boolean(dto.AssessmentNotificationActive),
    notifyMandatory: Boolean(dto.AssessmentMandatoryNotificationActive),
    groups: (dto.Groups ?? []).map((group) => ({
      value: group.GroupId,
      label: label(group.ArabicName, group.EnglishName),
      typeId: group.TypeId,
      kind: group.TypeId === GROUP_TYPE.DEPARTMENTS ? 'departments' : 'groups',
    })),
    categories: (dto.Categories ?? []).map((category) => ({
      value: category.CategoryId,
      label: label(category.ArabicName, category.EnglishName),
      /* Public or private — the form filters the list by it. */
      publicity: category.Publicity ?? CATEGORY_PUBLICITY.PUBLIC,
    })),
  }
}

function toAssessmentRow(dto) {
  return {
    id: dto.Id,
    title: dto.Title || label(dto.ArabicName, dto.EnglishName),
    name: dto.ArabicName ?? '',
    nameEn: dto.EnglishName ?? '',
    descriptionAr: dto.ArabicDescription ?? '',
    descriptionEn: dto.EnglishDescription ?? '',
    /* Date inputs only accept ISO yyyy-MM-dd. The localized display values remain separate
       so opening an Arabic row never feeds yyyy/MM/dd HH:mm back into an HTML date input. */
    startDate: dto.StartDate ? String(dto.StartDate).slice(0, 10) : '',
    endDate: dto.EndDate ? String(dto.EndDate).slice(0, 10) : '',
    displayStartDate: dto.StartDateString ?? '',
    displayEndDate: dto.EndDateString ?? '',
    enabled: Boolean(dto.Enabled),
    /* THE TABLE READS `isActive`, and the form reads `enabled` — so the row carries both
       rather than the column reaching for a name the mapper never produced. Without it every
       assessment rendered as "معطل", including the ones the server sends as `Enabled: true`. */
    isActive: Boolean(dto.Enabled),
    mandatory: Boolean(dto.Mandatory),
    finished: Boolean(dto.Finished),
    categoryIds: dto.CategoryIds ?? [],
    groupIds: dto.GroupIds ?? [],
    success: dto.SuccessCount ?? 0,
    failure: dto.FailureCount ?? 0,
    absent: dto.UnattendedCount ?? 0,
    /* Same again: the columns are `passed`/`failed`/`absent`, and only the two that were
       renamed came back blank — `absent` filled in because its name happened to survive. */
    passed: dto.SuccessCount ?? 0,
    failed: dto.FailureCount ?? 0,

    /* THE EXAM SETTINGS. Both handlers return them and this mapper used to drop them, so
       the edit form opened on its own defaults and `SaveAssessment` wrote those back —
       every edit silently reset the question count to 10 and the pass mark to 60. */
    questionsCount: dto.QuestionsCount ?? 10,
    passLimit: dto.PassLimit ?? 50,
  }
}

export async function listAssessments(signal) {
  const rows = (await api.post(`${ASSESSMENTS}/list`, undefined, { signal })) ?? []
  const mapped = rows.map(toAssessmentRow)
  return { rows: mapped, total: mapped.length }
}

/**
 * One assessment.
 *
 * `assessmentId`, NOT `Id` — the endpoint reads `form["assessmentId"]`, so the field this
 * sent bound to nothing and every call asked for assessment 0 and was answered
 * ASSESSMENT_NOT_FOUND. It had no caller until the report screen, which is why it went
 * unnoticed; `delete` next door has always sent the right name.
 */
export async function getAssessment(id, signal) {
  const dto = await api.post(
    `${ASSESSMENTS}/get`,
    toFormData({ assessmentId: String(id) }),
    { signal },
  )
  return dto ? toAssessmentRow(dto) : null
}

/**
 * ONE ASSESSMENT'S RESULTS, by person.
 *
 * `scope` is `passed`, `failed` or `unattended`. Paged and searched on the SERVER: the
 * audience of an assessment is whole departments, and the unattended tab is usually most of
 * them, so this is one list the browser should not be holding in full.
 *
 * `length: 0` asks for the COUNT ALONE — the screen shows one scope and the totals of all
 * three, and the two it is not showing cost a `COUNT(*)` each rather than a page of rows.
 */
export async function getAssessmentReport(assessmentId, scope, options = {}, signal) {
  const { start = 0, length = 25, search = '', sort = null } = options

  const dto = await api.post(
    `${ASSESSMENTS}/report`,
    {
      AssessmentId: Number(assessmentId) || 0,
      Scope: scope,
      Start: Math.max(0, start),
      Length: Math.max(0, length),
      SearchValue: search.trim() || null,
      /* By the column's name, as the table sends it; the handler falls back to its own
         order — best score first, or by name for the unattended — for anything else. */
      SortBy: sort?.key ?? null,
      SortDesc: sort?.dir === 'desc',
    },
    { signal },
  )

  return {
    /* After the search — what the pager counts. */
    total: dto?.Total ?? 0,
    /* Before it — what the tab counts. */
    scopeTotal: dto?.ScopeTotal ?? 0,
    /* Everyone the assessment was set for. NOT the sum of the three scopes — a retake that
       completed twice is two rows in `passed` and one person here. */
    audienceTotal: dto?.AudienceTotal ?? 0,
    rows: (dto?.Rows ?? []).map((row) => ({
      id: row.UserName || String(row.RowNumber),
      number: row.RowNumber ?? 0,
      userName: row.UserName ?? '',
      fullName: row.FullName ?? '',
      email: row.Email ?? '',
      department: row.Department ?? '',
      employeeId: row.EmployeeId ?? '',
      /* Absent for the unattended, who never sat one. */
      examDate: row.ExamDate ?? '',
      percentage: row.Percentage == null ? null : Number(row.Percentage),
      passed: row.Passed ?? null,
    })),
  }
}

/** The same assessment by QUESTION — how each one was answered. Not paged; see the slice. */
export async function getAssessmentAnswers(assessmentId, signal) {
  const rows = (await api.post(
    `${ASSESSMENTS}/report/answers`,
    { AssessmentId: Number(assessmentId) || 0 },
    { signal },
  )) ?? []

  return rows.map((row) => ({
    id: String(row.QuestionId ?? row.RowNumber),
    number: row.RowNumber ?? 0,
    question: row.Question ?? '',
    category: row.Category ?? '',
    difficulty: row.Difficulty ?? '',
    answers: row.TotalAnswers ?? 0,
    correct: row.CorrectAnswers ?? 0,
    wrong: row.WrongAnswers ?? 0,
    correctPercentage: Number(row.CorrectPercentage ?? 0),
  }))
}

export async function saveAssessment(values) {
  const fields = {
    Id: String(values.id ?? 0),
    ArabicName: values.name ?? '',
    EnglishName: values.nameEn ?? '',
    ArabicDescription: values.descriptionAr ?? '',
    EnglishDescription: values.descriptionEn ?? '',
    StartDate: values.startDate ?? '',
    EndDate: values.endDate ?? '',
    Enabled: values.enabled ? 'true' : 'false',
    Mandatory: values.mandatory ? 'true' : 'false',
    // Comma-separated, not lists.
    CategoryIds: (values.categoryIds ?? []).join(','),
    GroupIds: (values.groupIds ?? []).join(','),
    QuestionsCount: String(values.questionsCount ?? 10),
    PassLimit: String(values.passLimit ?? 50),
    BaselineAssessmentId: values.baselineId ? String(values.baselineId) : '',
    /* 0 = Add, 1 = Update (`SaveAssessmentCommand`). Defaulting to 0 meant EDITING an
       assessment took the add branch: the original was left untouched and a second row
       appeared beside it under the new name. An id is what distinguishes the two. */
    OperationType: String(values.operationType ?? (values.id ? 1 : 0)),
  }

  try {
    const data = await api.post(`${ASSESSMENTS}/save`, toFormData(fields))
    return { ok: data?.Result !== false, message: data?.Message ?? '', id: data?.Id ?? values.id }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

async function assessmentAction(path, fields) {
  try {
    const data = await api.post(`${ASSESSMENTS}/${path}`, toFormData(fields))
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/**
 * `assessmentId`, NOT `Id` — the same trap the feature switch above carries a note about.
 * `DeleteAssessmentCommand.AssessmentId` binds from `form["assessmentId"]`, so sending `Id`
 * bound 0 and the handler answered "لم يتم العثور على الاختبار" against an assessment that
 * does not exist. The confirmation was accepted and the row simply stayed.
 */
export const deleteAssessment = (id) =>
  assessmentAction('delete', { assessmentId: String(id) })

/**
 * Whether assessments are offered at all.
 *
 * `isEnabled`, NOT `Enabled` — the same trap the surveys switch carries a note
 * about further down. The endpoint reads `form["isEnabled"]` and the field was
 * being sent as `Enabled`, so `bool.TryParse("")` failed and the handler wrote
 * `false` on EVERY press. It then returned `Result: true` regardless, so the
 * admin saw a success message, the switch stayed on — and the learner panel,
 * which gates its nav on `Assessment_IsEnabled`, never saw assessments appear.
 * Turning the feature OFF had always worked, by accident.
 */
export const setAssessmentsEnabled = (enabled) =>
  assessmentAction('update-feature-state', { isEnabled: enabled ? 'true' : 'false' })

/** GET Awareness?id= — one bulletin, with its videos and attachments. */
/** One item. The list endpoint narrowed to a single id — there is no get-one. */
export async function getAwareness(id, signal) {
  const rows = (await api.post(`${AWARENESS}/list`, toFormData({ AwarenessId: String(id) }), { signal })) ?? []
  const row = rows.map(toAwarenessRow)[0] ?? null
  if (!row) return null

  return { ...row, attachments: await listAttachments(id, signal), videos: [] }
}

/** The files hung off one item. */
export async function listAttachments(awarenessId, signal) {
  try {
    const rows =
      (await api.post(`${AWARENESS}/attachments`, toFormData({ AwarenessId: String(awarenessId) }), { signal })) ?? []

    /* `NameAr`/`NameEn`, not `ArabicName`/`EnglishName` — the old names read undefined and
       the form's list drew blank rows. The two download paths are relative to the app root
       and are what a saved attachment HAS; a queued one has files instead. */
    return rows.map((dto) => ({
      id: dto.Id,
      nameAr: dto.NameAr ?? '',
      nameEn: dto.NameEn ?? '',
      urlAr: dto.ArabicNameDl ?? '',
      urlEn: dto.EnglishNameDl ?? '',
      fileAr: null,
      fileEn: null,
    }))
  } catch (error) {
    if (error instanceof ApiError) return []
    throw error
  }
}

/** POST Awareness/Save — the whole bulletin, satellites included. */
/**
 * Create or update.
 *
 * @param {string} [values.imageBase64] the banner, base64 in a FORM FIELD — see the note
 *        at the top of this block.
 */
export async function saveAwareness(values) {
  /* THE COVER, WHICH NEVER LEFT THE BROWSER.
   *
   * The form's picker stores the chosen file on `values.cover`. This read
   * `values.imageBase64`, which nothing in the app has ever set — so `ImageBase64` went up
   * empty on every save, `SaveAwarenessHandler.SaveImageAsync` was never reached, and the
   * learner's `coverUrl()` resolved to a file that had not been written.
   *
   * `values.imageBase64` is still honoured, for a caller that arrives already encoded. */
  const cover = values.cover instanceof Blob ? values.cover : null
  const imageBase64 = cover ? await fileToBase64(cover) : values.imageBase64 ?? ''

  const fields = {
    Id: values.id ?? '',
    /* THE ARABIC TITLE, NOT THE DISPLAY ONE. `values.title` is whichever language the panel
       is in, so writing it here overwrites the Arabic with the English.
       Every caller now carries `titleAr`: the row the activate/deactivate toggle round-trips
       has it from `toAwarenessRow`, and the form edits it directly. It used to edit `title`
       instead, which is how an edited Arabic title was dropped — `titleAr` arrived with the
       row, was never touched, and won this expression. `title` stays only as a fallback for
       a value object built without one. */
    TitleAr: values.titleAr ?? values.title ?? '',
    TitleEn: values.titleEn ?? '',
    /* BASE64, not plain text. `SaveAwarenessHandler:35` runs both through
       `Convert.FromBase64String`, so HTML sent as itself comes back as
       "The input is not a valid Base-64 string" — which names nothing and points nowhere.
       The image beside them is base64 too, but that one tolerates a data-URL prefix. */
    HtmlContentAr: toBase64(values.content),
    HtmlContentEn: toBase64(values.contentEn),
    IsActive: values.isActive ? 'true' : 'false',
    // A comma-separated string, not a list.
    Groups: (values.groupIds ?? []).join(','),
    ImageBase64: imageBase64,
    /* The handler names the file `{id:N}.png` whatever it was given, so neither of these
       reaches disk — they are sent because the contract has the fields. */
    ImageName: cover?.name ?? values.imageName ?? '',
    ImageType: cover ? extensionOf(cover) : values.imageType ?? '',
  }

  try {
    const data = await api.post(`${AWARENESS}/save`, toFormData(fields))
    return { ok: data?.Result !== false, message: data?.Message ?? '', id: data?.Id ?? values.id }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

async function awarenessAction(path, fields) {
  try {
    const data = await api.post(`${AWARENESS}/${path}`, toFormData(fields))
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/**
 * Who has opened a bulletin — `POST .../views`, a DataTables page.
 *
 * Documented in this module's own header since it was written and never implemented, so an
 * administrator had no way to see whether a bulletin had been read. The data was always
 * there: `AWR-011` proves a learner's view IS recorded and returned.
 *
 * One page of `MAX_PAGE_SIZE`, like the other drawer lists here — a bulletin's readership is
 * the size of a department, not of a log.
 */
export async function listAwarenessViews(awarenessId, signal) {
  try {
    const dto = await api.post(
      `${AWARENESS}/views?start=0&length=${MAX_PAGE_SIZE}`,
      toFormData({ AwarenessId: String(awarenessId) }),
      { signal },
    )
    const { rows, total } = readDataTable(dto)

    return {
      total,
      rows: rows.map((row) => ({
        id: row.Id,
        userId: row.UserId,
        username: row.Username ?? '',
        email: row.Email ?? '',
        name: label(row.UserFullNameArabic, row.UserFullNameEnglish),
        // Already formatted server-side, and in Arabic that means the Hijri date.
        seenOn: row.Timestamp ?? '',
      })),
    }
  } catch (error) {
    if (error instanceof ApiError) return { rows: [], total: 0, message: error.message }
    throw error
  }
}

export const removeAttachment = (attachmentId) =>
  awarenessAction('remove-attachment', { AttachmentId: String(attachmentId) })

/**
 * Attachments are their OWN calls — `SaveAwareness` writes the bulletin and nothing else,
 * so a list edited in the form has to be posted piece by piece once the bulletin has an id.
 *
 * Both files are optional on both calls: add writes only the halves it is given, and update
 * keeps whichever file it is not sent. The ids are GUIDs, not numbers.
 */
export async function addAwarenessAttachment({ awarenessId, nameAr, nameEn, fileAr, fileEn }) {
  return awarenessAction('add-attachment', {
    AwarenessId: String(awarenessId),
    TitleAr: nameAr ?? '',
    TitleEn: nameEn ?? '',
    FileArBase64: await fileToBase64(fileAr),
    FileArName: fileAr?.name ?? '',
    FileEnBase64: await fileToBase64(fileEn),
    FileEnName: fileEn?.name ?? '',
  })
}

export async function updateAwarenessAttachment({ id, nameAr, nameEn, fileAr, fileEn }) {
  return awarenessAction('update-attachment', {
    AttachmentId: String(id),
    TitleAr: nameAr ?? '',
    TitleEn: nameEn ?? '',
    FileArBase64: await fileToBase64(fileAr),
    FileArName: fileAr?.name ?? '',
    FileEnBase64: await fileToBase64(fileEn),
    FileEnName: fileEn?.name ?? '',
  })
}

/** The screen's own settings — whether it is on, and what it is called. */
export const saveAwarenessScreenSettings = (settings) =>
  awarenessAction('save-settings', {
    IsEnabled: settings.enabled ? 'true' : 'false',
    AwarenessScreenNameAr: settings.screenNameAr ?? '',
    AwarenessScreenNameEn: settings.screenNameEn ?? '',
  })

